import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, CheckCircle2, Store, UserRound, Eye, EyeOff, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { pressZoom } from "@/lib/usePressZoom";
import ImageZoom from "@/components/ImageZoom";
import { RESTAURANT } from "@/lib/data";
import { useDiscounts, tierRate, nextTier, fmtRate } from "@/lib/useDiscounts";

const inputCls =
  "w-full bg-white border border-[#6B1226]/15 rounded-xl px-4 py-3 text-base outline-none focus:border-[#6B1226]/50 transition-colors";

function AuthGate() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", phone: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Ravi de vous revoir ${u.first_name || u.name}`);
      } else {
        const u = await register({ first_name: form.firstName, last_name: form.lastName, email: form.email, password: form.password, phone: form.phone || undefined });
        toast.success(`Bienvenue ${u.first_name || u.name}, vous pouvez commander`);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 bg-white rounded-2xl border border-[#6B1226]/15 p-5" data-testid="cart-auth-gate">
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-full bg-[#6B1226]/10 flex items-center justify-center text-[#6B1226]">
          <UserRound size={20} />
        </span>
        <p className="text-sm text-[#1C1917] leading-snug">
          <span className="font-semibold">Un compte est nécessaire pour commander.</span>
          <br />
          <span className="text-[#57534E] text-xs">La navigation reste libre, sans coordonnées.</span>
        </p>
      </div>
      <form onSubmit={submit} className="space-y-2.5">
        {mode === "register" && (
          <>
            <div className="grid grid-cols-2 gap-2.5">
              <input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="Prénom" className={inputCls} data-testid="cart-auth-firstname" />
              <input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Nom" className={inputCls} data-testid="cart-auth-lastname" />
            </div>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone mobile" className={inputCls} data-testid="cart-auth-phone" />
          </>
        )}
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="cart-auth-email" />
        <div className="relative">
          <input required type={showPassword ? "text" : "password"} minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe (6 caractères min.)" className={`${inputCls} pr-11`} data-testid="cart-auth-password" />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B1226]/50 hover:text-[#6B1226] transition-colors"
            data-testid="cart-auth-password-toggle"
            aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
          >
            {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>
        {error && (
          <p className="text-xs text-[#6B1226] bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-xl px-3 py-2.5" data-testid="cart-auth-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} className="w-full rounded-full bg-[#6B1226] text-[#FAF6F0] py-3 text-sm font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-60" data-testid="cart-auth-submit">
          {busy ? "Un instant…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
        </button>
        <button
          type="button"
          onClick={() => {
            setMode(mode === "login" ? "register" : "login");
            setError("");
          }}
          className="w-full text-center text-xs text-[#6B1226] font-semibold hover:underline"
          data-testid="cart-auth-toggle"
        >
          {mode === "login" ? "Pas encore de compte ? Créez-le" : "Déjà client ? Connectez-vous"}
        </button>
      </form>
    </div>
  );
}

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, total, clear } = useCart();
  const { user } = useAuth();
  const [step, setStep] = useState("cart");
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [zoomSrc, setZoomSrc] = useState(null);
  const [zoomHover, setZoomHover] = useState(false);
  const [needBirth, setNeedBirth] = useState(false);
  const [birthDate, setBirthDate] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const tiersMap = useDiscounts();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    postalCode: "",
    city: "",
    mobile: "",
    note: "",
    company: "",
    bill1: "",
    bill2: "",
    billPostal: "",
    billCity: "",
  });
  // La fiche d'informations est fermée par défaut : le client l'ouvre lui-même s'il veut compléter
  const [infoOpen, setInfoOpen] = useState(false);
  // Facturation société : facultative, refermée par défaut dans la fiche
  const [billingOpen, setBillingOpen] = useState(false);

  const todayIso = new Date().toISOString().slice(0, 10);
  const PICKUP_INFO = "Notre service vous informera dès que votre commande sera prête à retirer.";

  const totalQty = items.reduce((s, i) => s + i.qty, 0);

  // Remise panachée par producteur + progression vers le palier suivant
  const byProducer = items.reduce((m, i) => {
    const p = i.wine.producer || "";
    m[p] = (m[p] || 0) + i.qty;
    return m;
  }, {});
  let discount = 0;
  const progress = [];
  Object.entries(byProducer).forEach(([producer, qty]) => {
    const tiers = tiersMap[producer] || [];
    const r = tierRate(tiers, qty);
    if (r > 0) {
      discount += r * qty;
      progress.push({ producer, qty, rate: r });
    } else {
      const nt = nextTier(tiers, qty);
      if (nt) progress.push({ producer, qty, next: nt });
    }
  });
  discount = Math.round(discount * 100) / 100;
  const finalTotal = Math.round((total - discount) * 100) / 100;

  useEffect(() => {
    if (open) {
      setStep("cart");
      setError("");
      if (user) {
        setForm((f) => ({
          ...f,
          firstName: f.firstName || user.first_name || "",
          lastName: f.lastName || user.last_name || "",
          mobile: f.mobile || user.phone || "",
          address1: f.address1 || user.address_line1 || "",
          address2: f.address2 || user.address_line2 || "",
          postalCode: f.postalCode || user.postal_code || "",
          city: f.city || user.city || "",
          note: f.note || user.preferred_note || "",
          company: f.company || user.company || "",
          bill1: f.bill1 || user.billing_address_line1 || "",
          bill2: f.bill2 || user.billing_address_line2 || "",
          billPostal: f.billPostal || user.billing_postal_code || "",
          billCity: f.billCity || user.billing_city || "",
        }));
        // Date de naissance demandée dès la deuxième commande
        if (!user.birth_date) {
          api
            .get("/orders/mine")
            .then((res) => setNeedBirth(res.data.length >= 1))
            .catch(() => {});
        } else {
          setNeedBirth(false);
        }
      }
    }
  }, [open, user]);

  const close = () => setOpen(false);

  const submit = async (e) => {
    e?.preventDefault();
    // Validation détaillée : on précise exactement ce qui manque au client
    const missing = [];
    if (!form.firstName.trim()) missing.push("votre prénom");
    if (!form.lastName.trim()) missing.push("votre nom");
    if (!form.mobile.trim()) missing.push("votre téléphone mobile");
    if (!form.address1.trim()) missing.push("votre adresse");
    if (!form.postalCode.trim()) missing.push("votre code postal");
    if (!form.city.trim()) missing.push("votre ville");
    if (needBirth && !birthDate) missing.push("votre date de naissance");
    if (missing.length) {
      setInfoOpen(true);
      setError(`Il manque : ${missing.join(", ")}.`);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("/orders", {
        customer_name: `${form.firstName} ${form.lastName}`.trim(),
        customer_phone: form.mobile,
        address_line1: form.address1,
        address_line2: form.address2 || undefined,
        postal_code: form.postalCode,
        city: form.city,
        pickup_time: PICKUP_INFO,
        note: form.note || undefined,
        birth_date: needBirth && birthDate ? birthDate : undefined,
        company: form.company || undefined,
        billing_address_line1: form.bill1 || undefined,
        billing_address_line2: form.bill2 || undefined,
        billing_postal_code: form.billPostal || undefined,
        billing_city: form.billCity || undefined,
        items: items.map((i) => ({ wine_id: i.wine.id, qty: i.qty })),
        qr_session: localStorage.getItem("qr_session") || undefined,
      });
      // Mémorise la préférence d'enlèvement et les coordonnées (reproposées à la prochaine commande)
      api.put("/auth/profile", {
        first_name: form.firstName,
        last_name: form.lastName,
        email: user.email,
        phone: form.mobile,
        address_line1: form.address1,
        address_line2: form.address2 || undefined,
        postal_code: form.postalCode,
        city: form.city,
        preferred_note: form.note || undefined,
        birth_date: user.birth_date || (needBirth && birthDate ? birthDate : undefined),
        company: form.company || undefined,
        billing_address_line1: form.bill1 || undefined,
        billing_address_line2: form.bill2 || undefined,
        billing_postal_code: form.billPostal || undefined,
        billing_city: form.billCity || undefined,
      }).catch(() => {});
      setOrder(res.data);
      clear();
      setStep("done");
      toast.success("Commande d'enlèvement confirmée");
    } catch (err) {
      const msg = formatApiError(err);
      if (msg === "BIRTH_DATE_REQUIRED") {
        setNeedBirth(true);
        setError("Vente d'alcool : merci d'indiquer votre date de naissance pour cette commande.");
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="checkout-drawer">
          <div className="absolute inset-0 bg-[#1C1917]/55 backdrop-blur-sm" onClick={close} />
          <motion.aside
            className="absolute right-0 top-0 h-full w-full sm:max-w-md bg-[#FAF6F0] shadow-2xl flex flex-col"
            style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#6B1226]/10">
              <div>
                <p className="eyebrow">Enlèvement à Belfort</p>
                <h2 className="font-display text-2xl font-semibold text-[#1C1917]">Ma commande</h2>
              </div>
              <button onClick={close} className="p-2 rounded-full hover:bg-[#6B1226]/10 text-[#1C1917] transition-colors" data-testid="cart-close-button" aria-label="Fermer">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 no-scrollbar" data-lenis-prevent>
              {step === "done" && order ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-10">
                  <CheckCircle2 size={56} className="text-[#6B1226]" />
                  <h3 className="font-display text-3xl font-semibold text-[#1C1917] mt-5">Commande confirmée</h3>
                  <p className="mt-2 font-eyebrow text-sm tracking-[0.25em] text-[#C5A059]" data-testid="order-confirmation-number">
                    N° {order.number}
                  </p>
                  <p className="mt-4 text-sm text-[#57534E] leading-relaxed max-w-xs">
                    Vos bouteilles vous attendent au restaurant. Présentez ce numéro lors du retrait.
                  </p>
                  <div className="mt-5 bg-white rounded-2xl border border-[#6B1226]/10 p-5 text-sm text-left w-full">
                    <p className="flex items-center gap-2 text-[#6B1226] font-semibold">
                      <Store size={15} /> {RESTAURANT.name}
                    </p>
                    <p className="mt-1.5 text-[#57534E]">{RESTAURANT.address}</p>
                    <p className="text-[#57534E]">{RESTAURANT.hours}</p>
                    {order.discount_total > 0 && (
                      <p className="mt-3 text-xs font-semibold text-[#3F6212]" data-testid="order-savings">
                        Vous avez gagné {eur(order.discount_total)} grâce à la remise panachée sur vos {order.total_qty} bouteilles
                      </p>
                    )}
                    <p className="mt-3 pt-3 border-t border-[#6B1226]/10 font-semibold text-[#1C1917]">
                      Total : {eur(order.total)}
                    </p>
                  </div>
                  <p className="mt-4 text-xs text-[#78716C]">Vos documents (bon de commande, facture) sont dans votre profil.</p>
                  <button onClick={close} className="mt-5 w-full rounded-full bg-[#6B1226] text-[#FAF6F0] py-3.5 text-sm font-semibold hover:bg-[#580E1D] transition-colors" data-testid="order-done-close-button">
                    Continuer ma visite
                  </button>
                </div>
              ) : items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="cart-empty-state">
                  <Store size={44} className="text-[#C5A059]" />
                  <p className="font-display text-2xl font-semibold text-[#1C1917] mt-4">Votre panier est vide</p>
                  <p className="text-sm text-[#57534E] mt-2 max-w-[240px]">
                    La cave regorge de belles bouteilles qui n'attendent que vous.
                  </p>
                  <button
                    onClick={() => {
                      close();
                      window.location.href = "/vins";
                    }}
                    className="mt-6 rounded-full bg-[#6B1226] text-[#FAF6F0] px-7 py-3 text-sm font-semibold hover:bg-[#580E1D] transition-colors"
                    data-testid="cart-browse-button"
                  >
                    Découvrir la cave
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex justify-end mb-3">
                    <button
                      onClick={() => {
                        if (!confirmClear) {
                          setConfirmClear(true);
                          return;
                        }
                        clear();
                        setConfirmClear(false);
                        toast.success("Commande remise à zéro");
                      }}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[0.65rem] font-semibold tracking-wide transition-colors ${
                        confirmClear
                          ? "bg-[#6B1226] text-[#FAF6F0]"
                          : "border border-stone-300 text-[#57534E] hover:border-[#6B1226] hover:text-[#6B1226]"
                      }`}
                      data-testid="cart-clear-button"
                      aria-label={confirmClear ? "Confirmer la remise à zéro de la commande" : "Vider ma commande"}
                    >
                      <RotateCcw size={12} /> {confirmClear ? "Confirmer : tout retirer ?" : "Remise à zéro de la commande"}
                    </button>
                  </div>
                  <ul className="space-y-4" data-testid="cart-items-list">
                    {items.map((item) => (
                      <li key={item.wine.id} className="flex gap-3 bg-white rounded-2xl border border-[#6B1226]/10 p-3" data-testid="cart-item">
                        <div className="w-16 h-20 shrink-0 flex items-center justify-center">
                          <img
                            src={item.wine.image}
                            alt={item.wine.name}
                            {...pressZoom(() => {
                              setZoomHover(true);
                              setZoomSrc(item.wine.image);
                            })}
                            onClick={(e) => {
                              e.stopPropagation();
                              setZoomHover(false);
                              setZoomSrc(item.wine.image);
                            }}
                            className="max-h-full w-auto object-contain no-frame cursor-zoom-in bottle-hover"
                            data-testid="cart-item-image"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-semibold text-[#1C1917] truncate">{item.wine.name}</p>
                          <p className="text-xs text-[#78716C]">{eur(item.wine.price)} / bouteille</p>
                          <div className="mt-2 flex items-center justify-between">
                            <div className="flex items-center border border-[#6B1226]/20 rounded-full">
                              <button onClick={() => setQty(item.wine.id, item.qty - 1)} className="p-1.5 px-2.5 text-[#6B1226]" data-testid="cart-item-minus" aria-label="Moins">
                                <Minus size={13} />
                              </button>
                              <span className="w-6 text-center text-sm font-semibold">{item.qty}</span>
                              <button onClick={() => setQty(item.wine.id, item.qty + 1)} className="p-1.5 px-2.5 text-[#6B1226]" data-testid="cart-item-plus" aria-label="Plus">
                                <Plus size={13} />
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="price text-[#6B1226]">{eur(item.wine.price * item.qty)}</span>
                              <button onClick={() => remove(item.wine.id)} className="p-1.5 text-stone-400 hover:text-[#6B1226] transition-colors" data-testid="cart-item-remove" aria-label="Retirer">
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {progress.length > 0 && (
                    <div className="mt-4 space-y-2" data-testid="cart-discount-line">
                      {progress.map((p) =>
                        p.rate ? (
                          <p key={p.producer || "x"} className="text-xs text-[#3F6212] bg-[#3F6212]/8 border border-[#3F6212]/25 rounded-xl px-4 py-2.5">
                            Remise {p.producer} débloquée : {fmtRate(p.rate)} × {p.qty} bt = −{eur(Math.round(p.rate * p.qty * 100) / 100)}
                          </p>
                        ) : (
                          <p key={p.producer || "x"} className="text-xs text-[#8C6D23] bg-[#F5EEDC] border border-[#C5A059]/40 rounded-xl px-4 py-2.5">
                            {p.producer} : plus que {p.next.threshold - p.qty} bouteille{p.next.threshold - p.qty > 1 ? "s" : ""} pour gagner {fmtRate(p.next.rate)} sur chacune
                          </p>
                        ),
                      )}
                    </div>
                  )}

                  {!user ? (
                    <AuthGate />
                  ) : (
                    <form onSubmit={submit} className="mt-6 space-y-3.5" data-testid="checkout-form">
                      {/* Fiche client compacte — nom, prénom, email et portable toujours visibles ; le client l'ouvre s'il veut compléter */}
                      <div className="bg-white rounded-2xl border border-[#6B1226]/15 p-4" data-testid="checkout-client-card">
                        <div className="flex items-center gap-3">
                          <span className="w-11 h-11 rounded-full bg-[#6B1226] text-[#FAF6F0] flex items-center justify-center font-display text-lg font-semibold shrink-0">
                            {(form.firstName || " ").charAt(0).toUpperCase()}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-semibold text-sm text-[#1C1917] truncate" data-testid="checkout-client-name">
                              {form.firstName} {form.lastName}
                            </span>
                            <span className="block text-xs text-[#57534E] truncate" data-testid="checkout-client-email">{user.email}</span>
                            <span className="block text-xs text-[#57534E]" data-testid="checkout-client-phone">{form.mobile}</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => setInfoOpen((v) => !v)}
                            className="shrink-0 inline-flex items-center gap-1 rounded-full border border-[#C5A059]/60 text-[#8C6D23] px-3.5 py-1.5 text-[0.65rem] font-bold hover:bg-[#F5EEDC] transition-colors"
                            data-testid="checkout-info-toggle"
                          >
                            {infoOpen ? "Fermer" : "Compléter"}
                          </button>
                        </div>
                      </div>

                      {infoOpen && (
                        <div className="space-y-2.5" data-testid="checkout-info-panel">
                          <p className="eyebrow">Vos coordonnées complètes</p>
                          <div className="grid grid-cols-2 gap-2.5">
                            <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="Prénom" className={inputCls} data-testid="checkout-customer-firstname" />
                            <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Nom" className={inputCls} data-testid="checkout-customer-lastname" />
                          </div>
                          <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Téléphone mobile" type="tel" className={inputCls} data-testid="checkout-customer-phone" />
                          <input value={form.address1} onChange={(e) => setForm({ ...form, address1: e.target.value })} placeholder="Adresse complète (n°, rue, bâtiment…)" className={inputCls} data-testid="checkout-address-line1" />
                          <input value={form.address2} onChange={(e) => setForm({ ...form, address2: e.target.value })} placeholder="Complément d'adresse (étage, digicode… facultatif)" className={inputCls} data-testid="checkout-address-line2" />
                          <div className="grid grid-cols-[5.5rem_1fr] gap-2.5">
                            <input inputMode="numeric" pattern="[0-9]{5}" maxLength={5} value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} placeholder="Code postal" className={inputCls} data-testid="checkout-postal-code" />
                            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" className={inputCls} data-testid="checkout-city" />
                          </div>
                          {needBirth && (
                            <div className="bg-[#F5EEDC] border border-[#C5A059]/40 rounded-xl p-3.5" data-testid="checkout-birthdate-block">
                              <label className="block text-xs font-bold text-[#8C6D23] mb-1.5">Votre date de naissance (obligatoire — vente d'alcool)</label>
                              <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} max={todayIso} className={inputCls} data-testid="checkout-birthdate" />
                            </div>
                          )}

                          {/* Facturation société — facultative, enrichit la fiche client */}
                          <button
                            type="button"
                            onClick={() => setBillingOpen((v) => !v)}
                            className="w-full flex items-center justify-between bg-[#F5EEDC]/60 border border-[#C5A059]/30 rounded-xl px-4 py-3 text-xs font-bold text-[#8C6D23] hover:bg-[#F5EEDC] transition-colors"
                            data-testid="checkout-billing-toggle"
                          >
                            <span>Facture au nom d'une société ? (facultatif)</span>
                            <span>{billingOpen ? "−" : "+"}</span>
                          </button>
                          {billingOpen && (
                            <div className="space-y-2.5" data-testid="checkout-billing-panel">
                              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Nom de la société" className={inputCls} data-testid="checkout-company" />
                              <input value={form.bill1} onChange={(e) => setForm({ ...form, bill1: e.target.value })} placeholder="Adresse de facturation" className={inputCls} data-testid="checkout-billing-address1" />
                              <input value={form.bill2} onChange={(e) => setForm({ ...form, bill2: e.target.value })} placeholder="Complément d'adresse de facturation (facultatif)" className={inputCls} data-testid="checkout-billing-address2" />
                              <div className="grid grid-cols-[5.5rem_1fr] gap-2.5">
                                <input inputMode="numeric" pattern="[0-9]{5}" maxLength={5} value={form.billPostal} onChange={(e) => setForm({ ...form, billPostal: e.target.value })} placeholder="Code postal" className={inputCls} data-testid="checkout-billing-postal" />
                                <input value={form.billCity} onChange={(e) => setForm({ ...form, billCity: e.target.value })} placeholder="Ville de facturation" className={inputCls} data-testid="checkout-billing-city" />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Enlèvement : pas de date à choisir — le service prévient le client */}
                      <div className="bg-[#F5EEDC]/70 rounded-2xl border border-[#C5A059]/40 p-4 flex items-start gap-3" data-testid="checkout-pickup-block">
                        <Store size={17} className="text-[#8C6D23] shrink-0 mt-0.5" />
                        <p className="text-xs text-[#44403C] leading-relaxed">
                          <span className="font-bold text-[#1C1917]">Enlèvement au restaurant — Le Bistroquet, Belfort.</span>
                          <br />
                          Notre service vous informera dès que votre commande sera prête à retirer.
                        </p>
                      </div>

                      {/* Message pour le sommelier — toujours visible */}
                      <textarea
                        value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        placeholder="Message pour le sommelier (facultatif)"
                        rows={2}
                        className={`${inputCls} resize-none`}
                        data-testid="checkout-note-input"
                      />

                      {error && (
                        <p className="text-sm text-[#6B1226] bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-xl px-4 py-3" data-testid="checkout-error">
                          {error}
                        </p>
                      )}
                    </form>
                  )}
                </>
              )}
            </div>

            {step !== "done" && items.length > 0 && (
              <div className="border-t border-[#6B1226]/10 px-6 py-5 bg-[#F4EFE6]" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}>
                <div className="space-y-1.5 mb-4 text-sm">
                  <div className="flex items-center justify-between text-[#57534E]">
                    <span>Sous-total ({totalQty} bt)</span>
                    <span>{eur(total)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex items-center justify-between text-[#8C6D23]">
                      <span>Remise panachée</span>
                      <span>−{eur(discount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1.5 border-t border-[#6B1226]/10">
                    <span className="text-[#57534E]">Total à régler sur place</span>
                    <span className="price text-2xl text-[#6B1226]" data-testid="cart-total">
                      {eur(finalTotal)}
                    </span>
                  </div>
                </div>
                {user ? (
                  <button
                    onClick={submit}
                    disabled={submitting}
                    className="w-full rounded-full bg-[#6B1226] text-[#FAF6F0] py-4 text-sm font-bold tracking-wide hover:bg-[#580E1D] transition-colors disabled:opacity-60"
                    data-testid="checkout-submit-button"
                  >
                    {submitting ? "Envoi en cours…" : "Confirmer l'enlèvement de ma commande"}
                  </button>
                ) : (
                  <p className="text-center text-xs text-[#78716C]" data-testid="checkout-login-hint">
                    Créez votre compte ci-dessus pour confirmer la commande
                  </p>
                )}
              </div>
            )}
          </motion.aside>
          <ImageZoom src={zoomSrc} alt="Bouteille" viaHover={zoomHover} onStick={() => setZoomHover(false)} onClose={() => setZoomSrc(null)} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
